import { FC, useMemo, useState } from 'react';
import CustomModal from 'components/custom-modal/CustomModal';
import { ServiceAreaInfo } from '../../jobs.model';
import { CircleCloseIcon, DropDownChevronDownIcon } from 'assets/icons';
import { ButtonType } from 'components/custom-button/CustomButton';
import { CustomButton, RadioWithLabel } from 'components';
import SearchBar from 'components/search/SearchBar';
import { metersToRoundedMiles } from 'utils/commonUtils';
import { selectCurrentTeamJobPermissions } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';

interface EditJobServiceAreaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (serviceAreaId: string) => void;
  selectedAreaId?: string;
  jobNumber: string;
  serviceAreas: ServiceAreaInfo[];
  onCreateClick: () => void;
}

const EditJobServiceAreaModal: FC<EditJobServiceAreaModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedAreaId,
  jobNumber,
  serviceAreas,
  onCreateClick
}) => {
  const { editJobSettings } = useAppSelector(selectCurrentTeamJobPermissions);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredServiceAreas = useMemo(() => {
    const filtered = serviceAreas?.filter(
      area => area.serviceAreaName.toLowerCase().includes(searchTerm.toLowerCase())
      // eslint-disable-next-line function-paren-newline
    );

    const withinAreas = filtered.filter(area => area.isEnclosing);
    const otherAreas = filtered.filter(area => !area.isEnclosing);

    return { withinAreas, otherAreas };
  }, [serviceAreas, searchTerm]);

  const handleSearchChange = (text: string) => {
    setSearchTerm(text);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  const renderServiceAreaItem = (area: ServiceAreaInfo) => (
    <div
      key={area.serviceAreaId}
      onClick={() => onSelect(area.serviceAreaId)}
      className="flex w-full cursor-pointer items-center justify-between py-3 pr-3 first-of-type:!pt-1">
      <RadioWithLabel
        isSelected={selectedAreaId === area.serviceAreaId}
        labelStyle={{ width: '100%' }}
        containerClassname="w-full"
        label={
          <div className="flex items-center text-sbase font-semibold text-primaryText">
            {area.serviceAreaName}
            {area.distance > 0 && (
              <div className="ml-1 text-gray-500">({metersToRoundedMiles(area.distance)} miles away)</div>
            )}
          </div>
        }
        onClick={() => null}
      />
      <div>
        <DropDownChevronDownIcon className="path path-fill-current h-[8px] w-[14px] -rotate-90 text-gray-1" />
      </div>
    </div>
  );

  return (
    <CustomModal
      open={isOpen}
      closeModal={handleClose}
      width="md:w-[510px]"
      height="h-auto min-h-[500px]"
      className="px-10 py-6"
      backgroundColor="bg-white">
      <div className="flex flex-col">
        <div className="relative mb-4 flex flex-col items-start justify-between">
          <div className="text-xl font-bold text-headingGray">Edit service area</div>
          <div className="mt-2 text-[15px] font-semibold text-headingGray">{jobNumber}</div>
          <CircleCloseIcon className="absolute right-0 top-0 cursor-pointer" onClick={handleClose} />
        </div>

        {/* Search Input */}
        <div className="mb-[15px]">
          <SearchBar
            value={searchTerm}
            handleChange={handleSearchChange}
            handleClear={handleClearSearch}
            placeholder="Search service area"
            autoFocus={false}
            showCrossIcon={true}
            id="service-area-search"
            magnifierIconStyle="w-4 h-4"
          />
        </div>

        {/* Create Service Area Button */}
        {editJobSettings && (
          <>
            <CustomButton
              className="h-6 w-full justify-start pb-1 pt-2 pl-0 pr-4 shadow-none"
              type={ButtonType.GHOST}
              childrenContainerStyles="text-sbase"
              onClick={onCreateClick}
              id="create-service-area-button">
              + Create service area
            </CustomButton>
            <div className="my-[15px] w-full border-b-[1px] border-secondaryBtn" />
          </>
        )}

        <div className="custom-scrollbar flex flex-col overflow-y-auto md:max-h-[300px]">
          {/* Within Service Areas Section */}
          {filteredServiceAreas.withinAreas.length > 0 && (
            <div className="mb-5">
              <div className="mb-2 text-sbase font-medium text-accentText">
                Within {filteredServiceAreas.withinAreas.length} service areas
              </div>
              <div className="divide-y">{filteredServiceAreas.withinAreas.map(renderServiceAreaItem)}</div>
            </div>
          )}

          {/* Other Service Areas Section */}
          {filteredServiceAreas.otherAreas.length > 0 && (
            <>
              <div className="mb-2 text-sbase font-medium text-accentText">Other service areas</div>
              <div className="divide-y">{filteredServiceAreas.otherAreas.map(renderServiceAreaItem)}</div>
              <div className="w-full border-b-[1px]" />
            </>
          )}

          {/* No Results */}
          {filteredServiceAreas.withinAreas.length === 0 &&
            filteredServiceAreas.otherAreas.length === 0 &&
            searchTerm && <div className="py-8 text-center text-gray-500">No service areas found</div>}
        </div>
      </div>
    </CustomModal>
  );
};

export default EditJobServiceAreaModal;
